// Stripe service — client-side helpers + Firebase callable wrappers
import Constants from 'expo-constants';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebaseConfig';
import {
  stripeUiTierFromHostname,
  effectiveStripeBillingMode,
  selectPublishableKey,
} from '../utils/stripeBillingMode';

// ── billing mode helpers ──────────────────────────────────────────────────────

/** Read Firestore stripe_settings and return { tier, mode, settings }. */
export async function getStripeBillingContext() {
  let settings = {};
  try {
    const snap = await getDoc(doc(db, 'site_config', 'stripe_settings'));
    if (snap.exists()) settings = snap.data() || {};
  } catch (e) {
    console.warn('[stripeService] stripe_settings read failed:', e?.message);
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const tier = stripeUiTierFromHostname(hostname);
  const mode = effectiveStripeBillingMode(tier, {
    useLiveInDevelopment: settings.useLiveInDevelopment === true,
    useLiveInStaging: settings.useLiveInStaging === true,
  });
  return { tier, mode, settings };
}

/** Returns 'live' or 'test' for the current session. */
export async function getStripeBillingMode() {
  const { mode } = await getStripeBillingContext();
  return mode;
}

/** Returns the correct Stripe publishable key (pk_test_ or pk_live_) for the current session. */
export async function getStripePublishableKeyForCurrentSession() {
  const extra = Constants?.expoConfig?.extra || Constants?.manifest?.extra || {};
  const mode = await getStripeBillingMode();
  return selectPublishableKey(extra, mode);
}

// ── plan constants ────────────────────────────────────────────────────────────
// buyUrlTest / buyUrlLive are fallbacks; the admin can set authoritative links
// in Firestore via Admin → Finances → Subscription Plans.

export const SUBSCRIPTION_PLANS = {
  BASIC: {
    id: 'basic_monthly',
    name: 'Basic',
    price: 9.99,
    currency: 'usd',
    buyUrlTest: 'https://buy.stripe.com/test_7sY8wJcO7fMN9qUeul7wA00',
    buyUrlLive: 'https://buy.stripe.com/fZu00c3YtbXq5RA6G53Nm00',
    interval: 'month',
    features: [
      '100 monthly queries',
      '2-leg SGP suggestions',
      'Limited markets (Moneyline/Spread/Total)',
      '2 sportsbooks selectable',
      'EV badge vs AI fair',
    ],
  },
  PRO: {
    id: 'pro_monthly',
    name: 'Pro',
    price: 29.99,
    currency: 'usd',
    buyUrlTest: 'https://buy.stripe.com/test_7sY4gt15pfMNfPidqh7wA01',
    buyUrlLive: 'https://buy.stripe.com/8x23cofHbgdGbbU2pP3Nm01',
    interval: 'month',
    features: [
      'Everything in Basic',
      '250 monthly queries',
      '2–3 leg SGP suggestions',
      'Wider markets (core player props)',
      '4 sportsbooks selectable',
      'CSV export of results',
      'Email + chat support',
    ],
  },
  ULTIMATE: {
    id: 'ultimate_monthly',
    name: 'Ultimate',
    price: 99.99,
    currency: 'usd',
    buyUrlTest: 'https://buy.stripe.com/test_00w14h9BV8kl0Uoae57wA02',
    buyUrlLive: 'https://buy.stripe.com/3cIeV63Yt2mQ7ZI5C13Nm02',
    interval: 'month',
    features: [
      'Everything in Pro',
      'Unlimited queries',
      'Full market set (team + player props)',
      'Multi-game parlays (configurable)',
      '8+ sportsbooks selectable',
      'Custom alerts (email/SMS)',
      'Priority support',
    ],
  },
};

// ── Stripe.js initialisation ──────────────────────────────────────────────────

/**
 * Returns subscription plans as an ordered array of { key, name, price, ... }.
 * Used by community MembersList to group/label members by their plan tier.
 */
export const getPlans = async () => {
  return Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => ({
    key: key.toLowerCase(),
    name: plan.name,
    price: plan.price,
    interval: plan.interval,
  }));
};

export const initializeStripe = async () => {
  try {
    const { loadStripe } = await import('@stripe/stripe-js');
    const key = await getStripePublishableKeyForCurrentSession();
    if (!key) {
      console.error('[Stripe] No publishable key for current mode. Set EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY_TEST / _LIVE in .env');
      return null;
    }
    return await loadStripe(key);
  } catch (error) {
    console.error('[Stripe] initializeStripe error:', error);
    return null;
  }
};

// ── checkout ──────────────────────────────────────────────────────────────────

/**
 * Start a Stripe subscription checkout for the given plan.
 *
 * Primary path: createCheckoutSession callable — server builds a Checkout Session
 * with the right price, promo code support, and A/B variant. This is the path
 * that supports dynamic pricing, discounts, and experiments.
 *
 * Fallback: payment link redirect — used when the user is not authenticated or
 * the callable fails (e.g. function cold-start timeout).
 */
export const handleSubscription = async (planKey, userEmail) => {
  const plan = SUBSCRIPTION_PLANS[planKey];
  if (!plan) throw new Error(`Unknown plan: ${planKey}`);

  // ── Primary: server-generated Checkout Session ──────────────────────────────
  // Requires the user to be Firebase-authenticated. The server applies the
  // correct price (including any active A/B experiment) and enables promo codes.
  try {
    const result = await _callable('createCheckoutSession')({
      planKey,
      successUrl: typeof window !== 'undefined' ? `${window.location.origin}/plans?subscribed=1` : undefined,
      cancelUrl: typeof window !== 'undefined' ? `${window.location.origin}/plans` : undefined,
    });
    const url = result?.data?.url;
    if (url) {
      window.location.href = url;
      return { success: true, plan: plan.name, variant: result?.data?.variant };
    }
  } catch (e) {
    // Not authenticated, or function unavailable — fall through to payment link
    console.warn('[handleSubscription] checkout session failed, falling back to payment link:', e?.message);
  }

  // ── Fallback: payment link redirect ─────────────────────────────────────────
  const mode = await getStripeBillingMode();
  let checkoutUrl = null;

  try {
    const snap = await getDoc(doc(db, '_app_config', 'subscriptions'));
    if (snap.exists()) {
      const planCfg = snap.data()?.plans?.[planKey] || {};
      if (mode === 'live') {
        const candidate = planCfg.stripePaymentLinkLive || '';
        checkoutUrl = candidate && !candidate.includes('test_') ? candidate : null;
      } else {
        checkoutUrl = planCfg.stripePaymentLink || planCfg.stripePaymentLinkTest || null;
      }
    }
  } catch {}

  if (!checkoutUrl) {
    checkoutUrl = mode === 'live' ? plan.buyUrlLive : plan.buyUrlTest;
  }

  if (!checkoutUrl) {
    throw new Error(
      mode === 'live'
        ? `No live payment link configured for ${plan.name}. Set stripePaymentLinkLive in Admin → Finances.`
        : `No test payment link configured for ${plan.name}.`
    );
  }

  const url = userEmail ? `${checkoutUrl}?prefilled_email=${encodeURIComponent(userEmail)}` : checkoutUrl;
  window.location.href = url;
  return { success: true, plan: plan.name, mode };
};

// ── Firebase callable wrappers ────────────────────────────────────────────────

function _callable(name) {
  return httpsCallable(getFunctions(getApp()), name);
}

/**
 * Cancel the current user's subscription at end of billing period.
 * Requires the user to be Firebase-auth'd (handled server-side).
 */
export const cancelSubscription = async () => {
  const result = await _callable('cancelSubscription')({});
  return result.data;
};

/**
 * Re-enable a subscription that was set to cancel_at_period_end.
 */
export const resumeSubscription = async () => {
  const result = await _callable('resumeSubscription')({});
  return result.data;
};

/**
 * Open the Stripe Customer Portal in a new tab.
 * The portal lets users manage payments, cancel, upgrade, download invoices etc.
 * @param {string} [returnUrl] - URL to return to after the portal session.
 */
export const openBillingPortal = async (returnUrl) => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const result = await _callable('createBillingPortalSession')({
    returnUrl: returnUrl || currentUrl,
  });
  const url = result?.data?.url;
  if (!url) throw new Error('No billing portal URL returned from server.');
  window.open(url, '_blank');
};

// ── legacy compat ─────────────────────────────────────────────────────────────

/** @deprecated Subscription status lives in the Firestore user doc (via AuthContext). */
export const getSubscriptionStatus = async () => null;

export const redirectToCheckout = async (url) => {
  window.location.href = url;
};
